import React, {useMemo} from 'react';
import {
	ReactHookFormSelectField,
	type ReactHookFormSelectFieldProps,
} from '@netx/core-components/Form/SelectField';
import type {ICustomFormItem, CustomFormFormValues} from '../../types';
import {
	getCustomFormItemTranslatedLabel,
	getCustomFormBaseFieldName,
} from '../../utils';
import {getSelectOptionDisabled, getSelectOptionLabel} from './utils';

export interface ReactHookFormCustomFormItemSelectProps<
	TValues extends CustomFormFormValues = CustomFormFormValues,
> extends Omit<
		ReactHookFormSelectFieldProps<string, false, false, false, TValues>,
		'label' | 'name' | 'required'
	> {
	item: ICustomFormItem;
}

export const ReactHookFormCustomFormItemSelectPrimitive = <
	TValues extends CustomFormFormValues = CustomFormFormValues,
>({
	item,
	...props
}: ReactHookFormCustomFormItemSelectProps<TValues>) => {
	const {label, value, required, userValue} = item;

	if (typeof value === 'string') return null;

	const options = useMemo(() => {
		return value.reduce<string[]>((accum, val) => {
			if (typeof val === 'string') return [...accum, val];
			if (val.disabled && val.key !== userValue) return accum;
			return [...accum, val.key];
		}, []);
	}, [value, userValue]);

	return (
		<ReactHookFormSelectField<string, false, false, false, TValues>
			{...props}
			disabled={props.disabled || item.disabled}
			fullWidth
			label={getCustomFormItemTranslatedLabel(label)}
			name={getCustomFormBaseFieldName(item)}
			options={options}
			required={required}
			getOptionDisabled={getSelectOptionDisabled(value)}
			getOptionLabel={getSelectOptionLabel(value)}
		/>
	);
};

export const ReactHookFormCustomFormItemSelect =
	ReactHookFormCustomFormItemSelectPrimitive;

export default ReactHookFormCustomFormItemSelect;
